/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { reactive, computed, watch, ref } from "vue";

// Layout modes
export const MENU_MODES = {
  STATIC: "static",
  DRAWER: "drawer",
  SLIM: "slim",
  SLIM_PLUS: "slim-plus",
  HORIZONTAL: "horizontal",
};

// Menu themes
export const MENU_THEMES = {
  LIGHT: "light",
  DARK: "dark",
  PRIMARY: "primary",
};

// Storage keys
const STORAGE_KEYS = {
  MENU_MODE: "layout-menu-mode",
  MENU_THEME: "layout-menu-theme",
  STATIC_MENU_INACTIVE: "layout-static-menu-inactive",
  DRAWER_ANCHORED: "layout-drawer-anchored",
  SIDEBAR_PROFILE_SHOW: "pristy-sidebar-profile-show",
  SIDEBAR_PROFILE_POSITION: "pristy-sidebar-profile-position",
};

// Default values
const DEFAULTS = {
  menuMode: MENU_MODES.STATIC,
  menuTheme: MENU_THEMES.LIGHT,
  staticMenuDesktopInactive: false,
  sidebarProfileShow: true,
  sidebarProfilePosition: "end",
};

// Breakpoints
const MOBILE_BREAKPOINT = 991;

// Reactive window width for responsive detection
const windowWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 1200,
);

// Detect if the device is a real mobile device (phone/tablet)
// This uses multiple detection methods for accuracy
const detectRealMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  // Check for touch capability (most reliable for modern devices)
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // Check User-Agent for mobile devices
  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera || "";
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
  const isMobileUserAgent = mobileRegex.test(userAgent);

  // Check for mobile platform
  const platform = navigator.platform || "";
  const mobilePlatforms = /iPhone|iPod|iPad|Android|webOS|BlackBerry/i;
  const isMobilePlatform = mobilePlatforms.test(platform);

  // A device is considered mobile if it has touch AND (mobile user agent OR mobile platform)
  // This prevents desktop browsers with touch screens from being detected as mobile
  return hasTouch && (isMobileUserAgent || isMobilePlatform);
};

// Cache the mobile device detection (it won't change during session)
const isRealMobileDevice = ref(detectRealMobileDevice());

// Update window width on resize
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

// Set up resize listener (call once)
let resizeListenerAdded = false;
const setupResizeListener = () => {
  if (typeof window !== "undefined" && !resizeListenerAdded) {
    window.addEventListener("resize", handleResize);
    resizeListenerAdded = true;
  }
};

// Global reactive state
const layoutState = reactive({
  menuMode: localStorage.getItem(STORAGE_KEYS.MENU_MODE) || DEFAULTS.menuMode,
  menuTheme:
    localStorage.getItem(STORAGE_KEYS.MENU_THEME) || DEFAULTS.menuTheme,
  staticMenuDesktopInactive:
    localStorage.getItem(STORAGE_KEYS.STATIC_MENU_INACTIVE) === "true" ||
    DEFAULTS.staticMenuDesktopInactive,
  anchored: localStorage.getItem(STORAGE_KEYS.DRAWER_ANCHORED) === "true",
  sidebarProfileShow:
    localStorage.getItem(STORAGE_KEYS.SIDEBAR_PROFILE_SHOW) !== "false",
  sidebarProfilePosition:
    localStorage.getItem(STORAGE_KEYS.SIDEBAR_PROFILE_POSITION) ||
    DEFAULTS.sidebarProfilePosition,
  mobileMenuActive: false,
  overlayMenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false,
  sidebarActive: false,
  activeMenuItem: null,
});

/**
 * useLayout composable for managing application layout
 * Inspired by PrimeVue Freya template
 */
export function useLayout() {
  // Watch for changes and persist to localStorage
  watch(
    () => layoutState.menuMode,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.MENU_MODE, newValue);
    },
  );

  watch(
    () => layoutState.menuTheme,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.MENU_THEME, newValue);
    },
  );

  watch(
    () => layoutState.staticMenuDesktopInactive,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.STATIC_MENU_INACTIVE, String(newValue));
    },
  );

  watch(
    () => layoutState.anchored,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.DRAWER_ANCHORED, String(newValue));
    },
  );

  watch(
    () => layoutState.sidebarProfileShow,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_PROFILE_SHOW, String(newValue));
    },
  );

  watch(
    () => layoutState.sidebarProfilePosition,
    (newValue) => {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_PROFILE_POSITION, newValue);
    },
  );

  // Watch for small screen transitions on desktop to handle sidebar
  // Only applies to desktop - mobile devices always use mobile layout
  watch(
    () => windowWidth.value <= MOBILE_BREAKPOINT,
    (isSmallScreen, wasSmallScreen) => {
      // Only handle screen size changes for desktop devices
      if (isRealMobileDevice.value) {
        return;
      }

      if (isSmallScreen && !wasSmallScreen) {
        // Entering small screen: reset sidebar states cleanly
        layoutState.sidebarActive = false;
        layoutState.overlayMenuActive = false;
        layoutState.staticMenuMobileActive = false;
      } else if (!isSmallScreen && wasSmallScreen) {
        // Leaving small screen: reset overlay states
        layoutState.staticMenuMobileActive = false;
        // Restore static sidebar visibility if needed
        if (layoutState.menuMode === MENU_MODES.STATIC) {
          layoutState.staticMenuDesktopInactive = false;
        }
      }
    },
  );

  // Computed properties
  const isSidebarActive = computed(() => {
    return (
      layoutState.overlayMenuActive ||
      layoutState.staticMenuMobileActive ||
      layoutState.sidebarActive
    );
  });

  const isDesktop = computed(() => {
    // Desktop = not a real mobile device
    return !isRealMobileDevice.value;
  });

  const isMobile = computed(() => {
    // Mobile layout only for real mobile devices (phones/tablets)
    // Desktop with small windows will NOT trigger mobile layout
    return isRealMobileDevice.value;
  });

  const isSmallScreen = computed(() => {
    // Check if screen is small (regardless of device type)
    return windowWidth.value <= MOBILE_BREAKPOINT;
  });

  const isStatic = computed(() => {
    return layoutState.menuMode === MENU_MODES.STATIC;
  });

  const isDrawer = computed(() => {
    return layoutState.menuMode === MENU_MODES.DRAWER;
  });

  const isSlim = computed(() => {
    return layoutState.menuMode === MENU_MODES.SLIM;
  });

  const isSlimPlus = computed(() => {
    return layoutState.menuMode === MENU_MODES.SLIM_PLUS;
  });

  const isHorizontal = computed(() => {
    return layoutState.menuMode === MENU_MODES.HORIZONTAL;
  });

  // Container class computed
  // Mobile layout only for real mobile devices
  // Desktop with small window gets layout-small-screen class for responsive adjustments
  const containerClass = computed(() => {
    const smallScreen = isSmallScreen.value && !isMobile.value;

    // On small screen desktop, force static+inactive behavior regardless of actual mode
    if (smallScreen) {
      return {
        "layout-static": true,
        "layout-small-screen": true,
        "layout-static-inactive": !layoutState.staticMenuMobileActive,
        "layout-mobile-active": layoutState.staticMenuMobileActive,
        [`layout-menu-${layoutState.menuTheme}`]: true,
        "p-input-filled": false,
        "p-ripple-disabled": false,
      };
    }

    return {
      "layout-static": isStatic.value,
      "layout-drawer": isDrawer.value,
      "layout-drawer-active": isDrawer.value && layoutState.sidebarActive,
      "layout-drawer-anchored": isDrawer.value && layoutState.anchored,
      "layout-slim": isSlim.value,
      "layout-slim-plus": isSlimPlus.value,
      "layout-horizontal": isHorizontal.value,
      "layout-mobile": isMobile.value,
      "layout-static-inactive":
        isStatic.value &&
        layoutState.staticMenuDesktopInactive &&
        isDesktop.value,
      "layout-overlay-active": layoutState.overlayMenuActive,
      "layout-mobile-active": layoutState.staticMenuMobileActive,
      "p-input-filled": false,
      "p-ripple-disabled": false,
      [`layout-menu-${layoutState.menuTheme}`]: true,
    };
  });

  // Methods
  const setMenuMode = (mode) => {
    if (Object.values(MENU_MODES).includes(mode)) {
      layoutState.menuMode = mode;
    }
  };

  const setMenuTheme = (theme) => {
    if (Object.values(MENU_THEMES).includes(theme)) {
      layoutState.menuTheme = theme;
    }
  };

  const setSidebarProfileShow = (show) => {
    layoutState.sidebarProfileShow = !!show;
  };

  const setSidebarProfilePosition = (position) => {
    if (["start", "end"].includes(position)) {
      layoutState.sidebarProfilePosition = position;
    }
  };

  const toggleMenu = () => {
    // On real mobile devices, toggle mobile menu
    if (isMobile.value) {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
      return;
    }

    // On desktop with small screen, use mobile-style toggle (sidebar overlay)
    if (isSmallScreen.value) {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
      return;
    }

    // On desktop with normal screen
    if (isStatic.value) {
      layoutState.staticMenuDesktopInactive =
        !layoutState.staticMenuDesktopInactive;
    } else if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
      layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
    }
  };

  const showMobileMenu = () => {
    layoutState.staticMenuMobileActive = true;
  };

  const hideMobileMenu = () => {
    layoutState.staticMenuMobileActive = false;
  };

  const hideOverlayMenu = () => {
    layoutState.overlayMenuActive = false;
  };

  const onMenuToggle = () => {
    toggleMenu();
  };

  const onSidebarMouseEnter = () => {
    if (isSmallScreen.value) {
      return;
    }
    if (isDrawer.value && !layoutState.anchored) {
      layoutState.sidebarActive = true;
    }
  };

  const onSidebarMouseLeave = () => {
    if (isSmallScreen.value) {
      return;
    }
    if (isDrawer.value && !layoutState.anchored) {
      layoutState.sidebarActive = false;
    }
  };

  const toggleAnchor = () => {
    layoutState.anchored = !layoutState.anchored;
    if (layoutState.anchored) {
      layoutState.sidebarActive = true;
    }
  };

  const setActiveMenuItem = (item) => {
    layoutState.activeMenuItem = item;
  };

  const resetMenu = () => {
    layoutState.overlayMenuActive = false;
    layoutState.staticMenuMobileActive = false;
    layoutState.menuHoverActive = false;
  };

  const onDocumentClick = (event) => {
    // Close mobile menu when clicking outside
    if (layoutState.staticMenuMobileActive) {
      const sidebarEl = document.querySelector(".layout-sidebar");
      const topbarMenuButtonEl = document.querySelector(
        ".layout-topbar-menu-button",
      );

      if (
        sidebarEl &&
        !sidebarEl.contains(event.target) &&
        topbarMenuButtonEl &&
        !topbarMenuButtonEl.contains(event.target)
      ) {
        hideMobileMenu();
      }
    }
  };

  // Apply layout settings (call on mount)
  const applyLayoutSettings = () => {
    // Set up resize listener for responsive detection
    setupResizeListener();

    // Re-read from localStorage in case values changed
    const savedMode = localStorage.getItem(STORAGE_KEYS.MENU_MODE);
    const savedTheme = localStorage.getItem(STORAGE_KEYS.MENU_THEME);
    const savedInactive = localStorage.getItem(
      STORAGE_KEYS.STATIC_MENU_INACTIVE,
    );

    if (savedMode && Object.values(MENU_MODES).includes(savedMode)) {
      layoutState.menuMode = savedMode;
    }
    if (savedTheme && Object.values(MENU_THEMES).includes(savedTheme)) {
      layoutState.menuTheme = savedTheme;
    }
    if (savedInactive !== null) {
      layoutState.staticMenuDesktopInactive = savedInactive === "true";
    }

    const savedProfileShow = localStorage.getItem(
      STORAGE_KEYS.SIDEBAR_PROFILE_SHOW,
    );
    if (savedProfileShow !== null) {
      layoutState.sidebarProfileShow = savedProfileShow !== "false";
    }
    const savedProfilePosition = localStorage.getItem(
      STORAGE_KEYS.SIDEBAR_PROFILE_POSITION,
    );
    if (
      savedProfilePosition &&
      ["start", "end"].includes(savedProfilePosition)
    ) {
      layoutState.sidebarProfilePosition = savedProfilePosition;
    }
  };

  return {
    // State
    layoutState,

    // Constants
    MENU_MODES,
    MENU_THEMES,

    // Computed
    isSidebarActive,
    isDesktop,
    isMobile,
    isSmallScreen,
    isStatic,
    isDrawer,
    isSlim,
    isSlimPlus,
    isHorizontal,
    containerClass,

    // Methods
    setMenuMode,
    setMenuTheme,
    setSidebarProfileShow,
    setSidebarProfilePosition,
    toggleMenu,
    toggleAnchor,
    showMobileMenu,
    hideMobileMenu,
    hideOverlayMenu,
    onMenuToggle,
    onSidebarMouseEnter,
    onSidebarMouseLeave,
    setActiveMenuItem,
    resetMenu,
    onDocumentClick,
    applyLayoutSettings,
  };
}
